import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  FormGroup,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axiosInstance from '../../../utils/axios';
import { APIS } from '../../../utils/serviceUrls';
import { Event } from '../../../@types/event';
import { FormProvider, RHFTextField, RHFSwitch, RHFSelect } from '../../../components/hook-form';
import ProgramBatchesField from './ProgramBatchesField';
import { UploadAvatar, UploadSingleFile } from '../../../components/upload';
import Iconify from '../../../components/Iconify';
import TextMaxLine from '../../../components/TextMaxLine';
import { EventSchema } from './index';
import LectureQuizBuilder from './LectureQuizBuilder';
import LectureAssignmentBuilder from './LectureAssignmentBuilder';

type EventNewEditFormProps = {
  isEdit: boolean;
  currentEvent?: Event;
  handleClose(): void;
};

const emptyLecture = {
  title: '',
  title_ar: '',
  info: '',
  info_ar: '',
  speaker: '',
  speaker_ar: '',
  thumbnail: '',
  htmlUrl: '',
  htmlUrl_ar: '',
  labFile: '',
  recordingUrl: '',
  order: 0,
  status: 'published' as const,
  // Batch ids this lecture is hidden for (shows as "opening soon"). Set per
  // batch in the Batches editor.
  excludedBatches: [] as string[],
  quiz: {
    title: '',
    title_ar: '',
    duration: 0,
    attemptsLimit: 3,
    passingScore: 50,
    secondsPerQuestion: 0,
    questionsToServe: 0,
    active: true,
    excludedBatches: [],
    showAnswers: false,
    questions: [],
  },
};

const EventNewEditForm = ({ isEdit, currentEvent, handleClose }: EventNewEditFormProps) => {
  const { defaultValues: baseDefaults, NewEventSchema } = EventSchema(currentEvent);

  // The shared batch defaults leave `instructor` out, so the per-cohort
  // Instructor select opened on "None" and the value was never sent. Carry the
  // stored teacher in here (as a plain id string). A cohort without one stays
  // `undefined`, which the save omits, so the API keeps whatever it stores.
  const defaultValues = useMemo(
    () => ({
      ...baseDefaults,
      batches: ((baseDefaults as any).batches || []).map((b: any, i: number) => {
        const stored = (currentEvent?.batches?.[i] as any)?.instructor;
        return stored ? { ...b, instructor: String(stored?._id ?? stored) } : b;
      }),
    }),
    [baseDefaults, currentEvent]
  );
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<any>({
    resolver: yupResolver(NewEventSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({ control, name: 'lectures' });

  const values = watch();

  // Direct (pre-batch) assignments that still exist on this program. The
  // picker that created them is gone — students are enrolled through batches —
  // but the value stays in form state so saving does not silently wipe it.
  const legacyAssigned = ((values?.assignedUsers as string[]) || []).map(String);
  const legacyAssignedCount = legacyAssigned.length;

  // How many of those legacy students would lose ALL access if the array were
  // cleared — i.e. they are in no batch. Drives whether clearing is offered.
  const batchStudentIds = new Set(
    ((values?.batches as any[]) || []).flatMap((b: any) =>
      ((b?.students as any[]) || []).map((s: any) => String(s)),
    ),
  );
  const legacyNotInAnyBatch = legacyAssigned.filter((id) => !batchStudentIds.has(id)).length;

  // A program nobody can see: no batch students and no legacy direct grants.
  // Access fails CLOSED (an empty audience means nobody, despite what the old
  // schema comment claimed), so this is worth saying out loud before saving.
  const audienceIsEmpty = batchStudentIds.size === 0 && legacyAssignedCount === 0;

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };

  const uploadHtml = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.HTML, form);
    return response.data;
  };

  const uploadAny = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.ANY, form);
    return response.data;
  };

  const onSubmit = async (data: any) => {
    if (data.date) {
      data.date = new Date(data.date).toISOString();
    } else {
      delete data?.date;
    }
    // Normalise batches for the API: the DTO validates dates as ISO strings and
    // capacity as an int, so blank inputs must be dropped rather than sent as ''.
    // `_id` is preserved for existing batches so their stamped quiz history stays
    // attached (a new id would orphan it).
    if (Array.isArray(data.batches)) {
      data.batches = data.batches
        .filter((b: any) => (b?.name || '').trim())
        .map((b: any) => {
          const out: any = {
            name: String(b.name).trim(),
            active: b.active !== false,
            students: (b.students || []).map((s: any) => String(s)),
          };
          if (b._id) out._id = String(b._id);
          if ((b.name_ar || '').trim()) out.name_ar = String(b.name_ar).trim();
          if (b.startDate) out.startDate = new Date(b.startDate).toISOString();
          if (b.endDate) out.endDate = new Date(b.endDate).toISOString();
          const cap = Number(b.capacity);
          if (b.capacity !== '' && b.capacity !== null && Number.isFinite(cap)) {
            out.capacity = cap;
          }
          // Who runs the cohort: the instructor portal and grading scope on it.
          // A chosen teacher is sent as its id; "None" ('' from the select) is
          // sent as null, which the API treats as a deliberate removal; an
          // untouched unknown value is omitted so the API keeps what it has.
          if (b.instructor === '' || b.instructor === null) out.instructor = null;
          else if (b.instructor) out.instructor = String(b.instructor?._id ?? b.instructor);
          return out;
        });
    }
    try {
      if (currentEvent) await axiosInstance.patch(APIS.EVENTS.PATCH(currentEvent._id), data);
      else await axiosInstance.post(APIS.EVENTS.ADD, data);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      if (err?.message) {
        enqueueSnackbar(err?.message, { variant: 'error' });
      } else {
        enqueueSnackbar(!isEdit ? 'Unable to create Program!' : 'Unable to edit Program', {
          variant: 'error',
        });
      }
    }
  };

  useEffect(() => {
    console.log('Errors', errors);
  }, [errors]);

  return (
    <Stack height={'100%'} style={{ overflow: 'auto' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'0px'} marginTop={'0px'} fontSize={'22px'}>
          {isEdit ? ' Edit Program' : ' Create Program'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'auto'}>
          <Stack spacing={3} mt={2}>
            {/* Banner image */}
            <Stack alignItems={'center'} spacing={1}>
              <UploadAvatar
                accept={{ 'image/*': [] }}
                file={values.bannerImage}
                label={values.bannerImage ? 'Update banner' : 'Upload banner'}
                onDrop={async (accepted: File[]) => {
                  if (!accepted?.length) return;
                  const res = await uploadImage(accepted[0]);
                  setValue('bannerImage', res?.url);
                }}
              />
              <Typography variant="caption" color={'text.secondary'}>
                Banner image
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                name="slug"
                label="Slug"
                helperText="url path, e.g. ain-shams"
              />
              <RHFTextField name="order" label="Order" type="number" />
              <RHFTextField name="title" label="Title" />
              <RHFTextField name="title_ar" label="Arabic Title" />
              <RHFTextField name="subtitle" label="Subtitle" />
              <RHFTextField name="subtitle_ar" label="Arabic Subtitle" />
              <RHFTextField name="location" label="Location" />
              <RHFTextField name="location_ar" label="Arabic Location" />
              <RHFTextField
                name="date"
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
              <RHFTextField name="ctaText" label="CTA Text" />
              <RHFTextField name="ctaText_ar" label="Arabic CTA Text" />
            </Box>

            <FormGroup sx={{ display: 'grid', columnGap: 1, rowGap: 1 }}>
              <RHFTextField
                multiline
                minRows={3}
                maxRows={8}
                name="description"
                label="Description"
              />
              <RHFTextField
                multiline
                minRows={3}
                maxRows={8}
                name="description_ar"
                label="Arabic Description"
              />
            </FormGroup>

            <RHFSwitch name="active" label="Active" />

            {/* Students are assigned through BATCHES only. The old
                "Assign to users" picker created a second, competing way to
                enrol people: an admin had to assign everyone here AND then
                place them in a batch, and anyone assigned only here had no
                batch — so they were excluded from per-batch quiz analysis and
                from per-batch availability rules. The field is kept in form
                state (and therefore round-trips untouched on save) so existing
                direct assignments are preserved, but it is no longer editable
                here. */}
            <Alert severity="info" sx={{ mt: 1 }}>
              Students are enrolled through <strong>Batches</strong> below — add a batch and pick
              its students there.
              {audienceIsEmpty && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  <strong>Nobody can see this program yet.</strong> It has no batch students, so it
                  is hidden from every learner until you add some.
                </Typography>
              )}
              {legacyAssignedCount > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This program still has <strong>{legacyAssignedCount}</strong> student(s) assigned
                    directly, from before batches existed. They keep access, but they belong to no
                    batch — so they are missing from per-batch reports, and per-batch quiz rules do
                    not apply to them.
                  </Typography>

                  {/* Clearing is a REVOCATION, not tidy-up: for anyone not also
                      in a batch, assignedUsers is their ONLY route to the
                      program. Only offer the button once everyone has been
                      moved into a batch; until then, say exactly how many would
                      lose access. */}
                  {legacyNotInAnyBatch > 0 ? (
                    <Typography variant="body2" color="error.main">
                      <strong>{legacyNotInAnyBatch}</strong> of them are in no batch. Clearing now
                      would remove their access entirely — add them to a batch first, then the
                      clear option appears.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        All of them are also in a batch, so clearing is safe — batch membership
                        keeps their access.
                      </Typography>
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={() => setValue('assignedUsers', [])}
                      >
                        Clear direct assignments
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Alert>

            {/* Batches (cohorts) — membership + per-batch quiz analysis */}
            <Divider />
            <ProgramBatchesField />

            {/* Lectures editor */}
            <Divider />
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <TextMaxLine sx={{ fontSize: '16px', fontWeight: '700' }}>Lectures</TextMaxLine>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={() => append({ ...emptyLecture, order: fields.length })}
              >
                Add Lecture
              </Button>
            </Stack>

            {fields.map((field, index) => {
              const lecture = values.lectures?.[index] || {};
              return (
                <Card key={field.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    mb={1}
                  >
                    <Typography variant="subtitle2">Lecture {index + 1}</Typography>
                    <IconButton
                      size="small"
                      sx={{ color: 'error.main' }}
                      onClick={() => remove(index)}
                    >
                      <Iconify icon={'eva:trash-2-outline'} />
                    </IconButton>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 1,
                      rowGap: 1,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField name={`lectures.${index}.title`} label="Title" />
                    <RHFTextField name={`lectures.${index}.title_ar`} label="Arabic Title" />
                    <RHFTextField name={`lectures.${index}.speaker`} label="Speaker" />
                    <RHFTextField
                      name={`lectures.${index}.speaker_ar`}
                      label="Arabic Speaker"
                    />
                    <RHFTextField name={`lectures.${index}.order`} label="Order" type="number" />
                    <RHFSelect name={`lectures.${index}.status`} label="Status">
                      <option value="published">Published</option>
                      <option value="soon">Opening soon</option>
                    </RHFSelect>
                  </Box>

                  <FormGroup sx={{ display: 'grid', columnGap: 1, rowGap: 1, mt: 1 }}>
                    <RHFTextField
                      multiline
                      minRows={2}
                      maxRows={5}
                      name={`lectures.${index}.info`}
                      label="Info"
                    />
                    <RHFTextField
                      multiline
                      minRows={2}
                      maxRows={5}
                      name={`lectures.${index}.info_ar`}
                      label="Arabic Info"
                    />
                  </FormGroup>

                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 1,
                      mt: 2,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    {/* Lecture thumbnail */}
                    <Stack spacing={1}>
                      <Typography variant="caption" fontWeight={700}>
                        Thumbnail
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'image/*': [] }}
                        file={lecture.thumbnail}
                        label={'Select or drop image'}
                        onDropAccepted={async (accepted: File[]) => {
                          if (!accepted?.length) return;
                          const res = await uploadImage(accepted[0]);
                          setValue(`lectures.${index}.thumbnail`, res?.url);
                        }}
                      />
                      {lecture.thumbnail && (
                        <Link href={lecture.thumbnail} target="_blank" rel="noopener" variant="caption">
                          View thumbnail
                        </Link>
                      )}
                    </Stack>

                    {/* Lecture HTML file */}
                    <Stack spacing={1}>
                      <Typography variant="caption" fontWeight={700}>
                        Lecture HTML
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'text/html': ['.html', '.htm'] }}
                        file={lecture.htmlUrl}
                        label={'Select or drop .html file'}
                        onDropAccepted={async (accepted: File[]) => {
                          if (!accepted?.length) return;
                          const res = await uploadHtml(accepted[0]);
                          setValue(`lectures.${index}.htmlUrl`, res?.url);
                        }}
                      />
                      {lecture.htmlUrl ? (
                        <Link href={lecture.htmlUrl} target="_blank" rel="noopener" variant="caption">
                          Open current HTML
                        </Link>
                      ) : (
                        <Typography variant="caption" color={'text.disabled'}>
                          no HTML yet
                        </Typography>
                      )}

                      {/* Arabic lecture HTML (optional) — when present, the site shows an
                          EN/AR toggle and serves this file for Arabic viewers. */}
                      <Typography variant="caption" fontWeight={700} sx={{ mt: 1 }}>
                        Lecture HTML — Arabic (optional)
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'text/html': ['.html', '.htm'] }}
                        file={lecture.htmlUrl_ar}
                        label={'Select or drop the Arabic .html file'}
                        onDropAccepted={async (accepted: File[]) => {
                          if (!accepted?.length) return;
                          const res = await uploadHtml(accepted[0]);
                          setValue(`lectures.${index}.htmlUrl_ar`, res?.url);
                        }}
                      />
                      {lecture.htmlUrl_ar ? (
                        <Link href={lecture.htmlUrl_ar} target="_blank" rel="noopener" variant="caption">
                          Open current Arabic HTML
                        </Link>
                      ) : (
                        <Typography variant="caption" color={'text.disabled'}>
                          no Arabic HTML yet
                        </Typography>
                      )}

                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Lab file (download)
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'application/octet-stream': ['.ipynb', '.zip', '.pdf'] }}
                        file={lecture.labFile}
                        label={'Select or drop lab file (.ipynb, .zip, .pdf)'}
                        onDropAccepted={async (accepted: File[]) => {
                          if (!accepted?.length) return;
                          const res = await uploadAny(accepted[0]);
                          setValue(`lectures.${index}.labFile`, res?.url);
                        }}
                      />
                      {lecture.labFile ? (
                        <Link href={lecture.labFile} target="_blank" rel="noopener" variant="caption">
                          Current lab file
                        </Link>
                      ) : (
                        <Typography variant="caption" color={'text.disabled'}>
                          no lab file yet
                        </Typography>
                      )}

                      <Typography variant="subtitle2" sx={{ mt: 2 }}>
                        Session recording (link)
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="https://drive.google.com/... or OneDrive / YouTube link"
                        helperText="Paste the link to this session's recording. Students get a 'Recording' button that opens it. Leave empty to hide the button."
                        value={lecture.recordingUrl || ''}
                        onChange={(e) =>
                          setValue(`lectures.${index}.recordingUrl`, e.target.value, {
                            shouldDirty: true,
                          })
                        }
                      />
                    </Stack>
                  </Box>

                  <LectureQuizBuilder lectureIndex={index} />

                  <LectureAssignmentBuilder lectureIndex={index} />
                </Card>
              );
            })}

            {fields.length === 0 && (
              <Typography variant="body2" color={'text.disabled'}>
                No lectures yet. Add one to get started.
              </Typography>
            )}
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Program' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default EventNewEditForm;
